import { useEffect, useState } from 'react'

// Shared launch deadline so all countdowns on the page stay in sync.
// Persists a 48h window in localStorage from the user's first visit.
const STORAGE_KEY = 'megapack_deadline'
const WINDOW_MS = 48 * 60 * 60 * 1000

function getDeadline(): number {
  if (typeof window === 'undefined') return Date.now() + WINDOW_MS
  const stored = window.localStorage.getItem(STORAGE_KEY)
  if (stored) {
    const ts = parseInt(stored, 10)
    if (!Number.isNaN(ts) && ts > Date.now()) return ts
  }
  const deadline = Date.now() + WINDOW_MS
  window.localStorage.setItem(STORAGE_KEY, String(deadline))
  return deadline
}

export interface TimeLeft {
  hours: string
  minutes: string
  seconds: string
}

function format(ms: number): TimeLeft {
  const total = Math.max(0, ms)
  const totalSeconds = Math.floor(total / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => n.toString().padStart(2, '0')
  return { hours: pad(hours), minutes: pad(minutes), seconds: pad(seconds) }
}

export function useCountdown(): TimeLeft {
  const [deadline] = useState<number>(() => getDeadline())
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    format(deadline - Date.now()),
  )

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(format(deadline - Date.now()))
    }, 1000)
    return () => clearInterval(interval)
  }, [deadline])

  return timeLeft
}
